// src/hrms/alarms/AlarmHost.tsx
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlarmRingDialog, completionToast } from "./AlarmRingDialog";
import { completeFromAlarm, snoozeAlarm } from "./alarmActions";
import { startAlarmEngine } from "./alarmEngine";
import { ALARM_SW_MESSAGE, browserAlarmsAvailable, type AlarmNotificationAction } from "./alarmNotifications";
import { missedOccurrences, ringingOccurrences } from "./alarmSchedule";
import { installAlarmAudioUnlock, unlockAlarmAudio } from "./alarmSound";
import { loadAlarms } from "./alarmStore";
import type { AlarmItemKey } from "./alarmTypes";
import { useAlarmClock, useAlarmMap } from "./useAlarms";
import { isElectron } from "../../utils/electron";

/**
 * Whether the missed-alarms dialog is open. Module state, not React state:
 * the planner's banner opens it, and Layout (which hosts it) is rendered
 * afresh by each route.
 */
let missedOpen = false;
const missedListeners = new Set<() => void>();
function setMissedOpen(open: boolean) {
  missedOpen = open;
  missedListeners.forEach((listener) => listener());
}
export function openMissedAlarms() {
  setMissedOpen(true);
}
function subscribeMissed(listener: () => void) {
  missedListeners.add(listener);
  return () => {
    missedListeners.delete(listener);
  };
}
const getMissedOpen = () => missedOpen;

/** Missed occurrences this tab has already announced, so a route change does not toast them again. */
const announcedMissed = new Set<string>();

export function useMissedAlarmCount() {
  const { alarms, ownerId } = useAlarmMap();
  const now = useAlarmClock();
  return ownerId ? missedOccurrences(alarms, now).length : 0;
}

/**
 * Mounted once in the app shell. Runs this tab's alarm engine for the
 * signed-in user and shows the in-page alarm dialog, and carries out answers
 * that arrive from a notification (through the service worker, or from the
 * URL: the worker had to open a new tab, or the desktop app's native
 * notification deep-linked here).
 */
export function AlarmHost() {
  const { alarms, ownerId } = useAlarmMap();
  const now = useAlarmClock(15_000);
  const navigate = useNavigate();
  const location = useLocation();
  const showMissed = useSyncExternalStore(subscribeMissed, getMissedOpen, getMissedOpen);
  const available = browserAlarmsAvailable();

  useEffect(() => {
    if (!ownerId || !available) return;
    installAlarmAudioUnlock();
    // The desktop app may play audio without a click first, and its window can ring from the tray before anyone clicks it.
    if (isElectron) unlockAlarmAudio();
    return startAlarmEngine(ownerId);
  }, [ownerId, available]);

  const ringing = useMemo(() => (ownerId ? ringingOccurrences(alarms, now) : []), [alarms, now, ownerId]);
  const missed = useMemo(() => (ownerId ? missedOccurrences(alarms, now) : []), [alarms, now, ownerId]);

  // Alarms that fell while the browser was closed (or rang out unanswered) are announced once.
  useEffect(() => {
    const fresh = missed.filter(({ itemKey, occurrence }) => !announcedMissed.has(`${itemKey}|${occurrence.id}`));
    if (!fresh.length) return;
    fresh.forEach(({ itemKey, occurrence }) => announcedMissed.add(`${itemKey}|${occurrence.id}`));
    toast.warning(fresh.length === 1 ? `Missed alarm: ${fresh[0].alarm.item.title}` : `You missed ${fresh.length} alarms`, {
      description: "TeamLio wasn't open to ring it.",
      action: { label: "Review", onClick: openMissedAlarms },
    });
  }, [missed]);

  useEffect(() => {
    if (showMissed && missed.length === 0) setMissedOpen(false);
  }, [missed.length, showMissed]);

  const handleAction = useCallback(
    (action: AlarmNotificationAction, itemKey: AlarmItemKey, occurrenceId: string) => {
      if (!ownerId) return;
      const alarm = loadAlarms(ownerId)[itemKey];
      if (!alarm || alarm.handled[occurrenceId]) return;
      if (action === "snooze") {
        snoozeAlarm(ownerId, itemKey, occurrenceId);
        toast.success(`Snoozed for ${alarm.snoozeMinutes} min`, { description: alarm.item.title });
      } else if (action === "complete") {
        void completeFromAlarm(ownerId, itemKey, occurrenceId).then(completionToast);
      } else if (missedOccurrences({ [itemKey]: alarm }, Date.now()).some(({ occurrence }) => occurrence.id === occurrenceId)) {
        // "show": a ringing alarm is already on screen; a missed one opens the missed list.
        openMissedAlarms();
      }
    },
    [ownerId]
  );

  // Clicks routed back from a notification by public/alarm-sw.js.
  useEffect(() => {
    if (!available || !("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== ALARM_SW_MESSAGE || data.ownerId !== ownerId) return;
      handleAction(data.action, data.itemKey, data.occurrenceId);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    // A listener added this way does not start the client's message queue in every browser (Safari); start it.
    navigator.serviceWorker.startMessages?.();
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [available, handleAction, ownerId]);

  // The worker found no open tab and opened this one with the answer in the URL.
  const handledUrl = useRef<string | null>(null);
  useEffect(() => {
    if (!ownerId) return;
    const params = new URLSearchParams(location.search);
    const action = params.get("alarmAction") as AlarmNotificationAction | null;
    const itemKey = params.get("alarmItem") as AlarmItemKey | null;
    const occurrenceId = params.get("alarmOccurrence");
    if (!action || !itemKey || !occurrenceId || handledUrl.current === location.search) return;
    handledUrl.current = location.search;
    ["alarmAction", "alarmItem", "alarmOccurrence"].forEach((key) => params.delete(key));
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    handleAction(action, itemKey, occurrenceId);
  }, [handleAction, location.pathname, location.search, navigate, ownerId]);

  if (!ownerId || !available) return null;
  if (ringing.length) {
    return <AlarmRingDialog entries={ringing} mode="ringing" now={now} ownerId={ownerId} onNavigate={navigate} />;
  }
  if (showMissed && missed.length) {
    return (
      <AlarmRingDialog
        entries={missed}
        mode="missed"
        now={now}
        ownerId={ownerId}
        onClose={() => setMissedOpen(false)}
        onNavigate={(path) => {
          setMissedOpen(false);
          navigate(path);
        }}
      />
    );
  }
  return null;
}
